import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Alert,
  UncontrolledAlert,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { showConfirmationModal } from '../actions/App';
import { fetchUserProfile } from '../actions/Auth';

import {
  APIgetShareSettings,
  APIupdateShareSettings,
} from '../api/Shares';
import {
  APIfetchUserProfileById,
  APIgetUserSettings,
  APIupdateUser,
  APIupdatePassword,
  APIcreateUser,
  APIremove2FA,
  APIconfirmEmailChange,
  APIcancelEmailChange,
  APIunlockUser,
} from '../api/User';

import AdminUserEditHeader from '../components/Admin/UserEdit/Header';
import AdminUserEditTabs from '../components/Admin/UserEdit/Tabs';
import AdminUserEditTabContent from '../components/Admin/UserEdit/TabContent';

import {
  getTabUrlParam,
  getUrlParam,
  removeUrlParam,
  updateTabUrlParam,
  parseResponseErrors,
  sendTrackingEvent,
} from '../helpers';
import { addFlashMessage } from '../helpers/FlashMessages';
import { performOnboardingStep } from '../helpers/Onboarding';
import { getUserSetting } from '../helpers/Settings';
import { sendWelcomeEmails } from '../helpers/Users';

import Loading from '../components/Loading';
import ErrorBoundary from '../components/ErrorBoundary';
import GoBackToListButton from '../components/GoBackToListButton';

class ProfileEdit extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeTab: getTabUrlParam() || 'profile',
      profile: {
        uuid: null,
        first_name: '',
        last_name: '',
        email: '',
        avatar_url: null,
        avatar_reset: false,
        role: null,
        role_id: null,
        send_welcome_email: true,
        note: null,
        fields: [],
        groups: [],
        notifications: [],
        workspace_owner: false,
      },
      roles: [],
      newAvatarUrl: null,
      errors: {},
      loading: true,
      loadingAddTemplate: false,
      loadingRemoveTemplate: false,
      rolesLoading: true,
      saving: false,
      confirmingEmailChange: false,
      emailChangeError: false,
      emailChangeConfirmed: false,
      notificationSound: getUserSetting('notification_sound'),
      viewModeSwitcher: false,
      settings: {},
      selectedTemplateUuid: null,
      templates: [],
      unsavedProfile: false,
      unsavedShareTemplates: false,
      prefixAndSuffix: getUserSetting('file_name_prefix_and_suffix'),
      unlocking: false,
    };

    this.inputRefs = {};
    this.inputRefs.currentPassword = React.createRef();
    this.inputRefs.password = React.createRef();
    this.inputRefs.passwordConfirmation = React.createRef();

    this.handleSwitchTab = this.handleSwitchTab.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleChangeAvatar = this.handleChangeAvatar.bind(this);
    this.handleChangeRole = this.handleChangeRole.bind(this);
    this.handleChangeNextPasswordChangeDate = this.handleChangeNextPasswordChangeDate.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleSaveAndInvite = this.handleSaveAndInvite.bind(this);
    this.handleSendWelcomeEmails = this.handleSendWelcomeEmails.bind(this);
    this.handleSwitchGroup = this.handleSwitchGroup.bind(this);
    this.handleRemove2FA = this.handleRemove2FA.bind(this);
    this.handleUnlock = this.handleUnlock.bind(this);
    this.handleCancelEmailChange = this.handleCancelEmailChange.bind(this);
    this.handleNotificationChange = this.handleNotificationChange.bind(this);
    this.handleNotificationSoundChange = this.handleNotificationSoundChange.bind(this);
    this.handleSwitchViewModeSwitcher = this.handleSwitchViewModeSwitcher.bind(this);
    this.handleShareTemplatesAdd = this.handleShareTemplatesAdd.bind(this);
    this.handleShareTemplatesChange = this.handleShareTemplatesChange.bind(this);
    this.handleShareTemplatesRemove = this.handleShareTemplatesRemove.bind(this);
    this.handleSelectedTemplateUuid = this.handleSelectedTemplateUuid.bind(this);
    this.handlePrefixAndSuffix = this.handlePrefixAndSuffix.bind(this);
    this.loadUserProfile = this.loadUserProfile.bind(this);
    this.loadShareTemplates = this.loadShareTemplates.bind(this);
    this.handleSaveShareTemplates = this.handleSaveShareTemplates.bind(this);
  }

  componentDidMount() {
    this.loadUserProfile();
    this.loadShareTemplates();
  }

  componentDidUpdate(prevProps) {
    const {
      match,
      loggedInUser,
    } = this.props;
    const {
      subTab,
      userUuid,
    } = match.params;

    if (
      userUuid !== prevProps.match.params.userUuid ||
      subTab !== prevProps.match.params.subTab
    ) {
      this.loadUserProfile();
    }

    if (loggedInUser.language !== prevProps.loggedInUser.language) {
      this.loadUserProfile(true);
    }
  }

  confirmEmailChange() {
    this.setState({
      confirmingEmailChange: true,
      emailChangeError: false,
    });

    return APIconfirmEmailChange(getUrlParam('email_confirmation_code'))
      .then(() => {
        this.setState(prevState => ({
          profile: {
            ...prevState.profile,
            email: prevState.profile.pending_email,
            pending_email: null,
          },
          emailChangeConfirmed: true,
          confirmingEmailChange: false,
        }), () => {
          removeUrlParam('email_confirmation_code');
        });
      }).catch((error) => {
        this.setState({
          confirmingEmailChange: false,
          emailChangeError: error.response.data.message,
        });
      });
  }

  handleCancelEmailChange(e) {
    const { intl } = this.props;
    e.preventDefault();

    this.setState(prevState => ({
      profile: {
        ...prevState.profile,
        pending_email: null,
      },
    }));

    addFlashMessage({
      type: 'success',
      text: intl.formatMessage({ defaultMessage: 'Your email change request has been canceled' }),
    });

    return APIcancelEmailChange();
  }

  loadUserProfile(silent = false) {
    const {
      match,
      loggedInUser,
    } = this.props;
    const { userUuid } = match.params;
    const { subTab } = match.params;
    const editedUserUuid = userUuid || loggedInUser.uuid;

    if (subTab === 'add') {
      this.setState({ loading: false });
      return;
    }

    if (!silent) {
      this.setState({ loading: true });
    }

    APIfetchUserProfileById(editedUserUuid, { all: true }).then((response) => {
      const profile = (response && response.data) ? response.data : null;
      profile.current_password = '';
      profile.new_password = '';
      profile.password_confirmation = '';

      if (profile.pending_email && getUrlParam('email_confirmation_code')) {
        this.confirmEmailChange();
      }

      APIgetUserSettings(editedUserUuid, { editable: true }).then((settings) => {
        const settingsData = settings.data || {};
        this.setState({
          profile,
          notificationSound: profile.settings.notification_sound,
          viewModeSwitcher: profile.settings.view_mode_switcher,
          settings: settingsData || {},
          loading: false,
          unlocking: false,
        });
      });
    });
  }

  async loadShareTemplates() {
    try {
      const response = await APIgetShareSettings({ type: 'user' });
      this.setState({ templates: response.data.share_templates });
    } catch (e) {
      this.setState({ templates: [] });
    }
  }

  async handleSaveShareTemplates(silent = false) {
    const { intl } = this.props;
    const { templates, selectedTemplateUuid } = this.state;

    const newTemplates = templates.map(p => {
      if (p.touched) {
        const uuid = uuidv4();
        if (selectedTemplateUuid === p.uuid) {
          this.setState({ selectedTemplateUuid: uuid });
        }
        return {
          ...p,
          uuid,
        };
      }
      return p;
    });

    this.setState({
      saving: true,
      templates: newTemplates,
    });

    try {
      await APIupdateShareSettings({ type: 'user', templates: newTemplates });
      if (!silent) {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The changes have been saved' }),
        });
      }
    } finally {
      this.setState({
        saving: false,
        loadingAddTemplate: false,
        loadingRemoveTemplate: false,
        unsavedShareTemplates: false,
      });
    }
  }

  isSelfEdit() {
    const { loggedInUser } = this.props;
    const { profile } = this.state;

    return loggedInUser?.uuid === profile?.uuid;
  }

  handleErrors(error) {
    const { intl } = this.props;

    this.setState({
      saving: false,
      errors: parseResponseErrors(error.response),
    });
    addFlashMessage({
      type: 'danger',
      text: intl.formatMessage({
        defaultMessage: '<strong>Something went wrong.</strong>{br}Please check for error messages.',
      }, {
        strong: msg => <strong>{msg}</strong>,
        br: '\n',
      }),
    });
  }

  handleSwitchTab(tab) {
    this.setState({ activeTab: tab });
    updateTabUrlParam(tab);

    if (tab === '2fa') {
      sendTrackingEvent('2fa-tab-open');
    }
  }

  handleChange(e) {
    const {
      value,
      name,
      checked,
    } = e.target;
    const {
      profile,
      settings,
    } = this.state;

    if ([
      'first_name', 'last_name', 'email', 'note',
      'current_password', 'new_password', 'password_confirmation',
      'g2fa_code',
    ].includes(name)) {
      profile[name] = value;

      if (!['current_password'].includes(name)) {
        this.setState({ unsavedProfile: true });
      }
    }

    if (name.indexOf('userField') === 0) {
      const userFieldId = parseInt(name.replace('userField', ''));
      const userFieldIdx = profile.fields.findIndex(f => f.id === userFieldId);
      if (userFieldIdx >= 0) {
        profile.fields[userFieldIdx].value = value;
      } else {
        profile.fields.push({
          id: userFieldId,
          value,
        });
      }
      this.setState({ unsavedProfile: true });
    }

    if (name.indexOf('setting-') === 0) {
      const userSettingKey = name.replace('setting-', '');
      switch (settings[userSettingKey].type) {
        case 'boolean':
          settings[userSettingKey].value = checked;
          break;
        default:
          settings[userSettingKey].value = value;
      }
      this.setState({ unsavedProfile: true });
    }

    if (name === 'send_welcome_email') {
      profile.send_welcome_email = checked;
      this.setState({ unsavedProfile: true });
    }

    this.setState({
      profile,
      settings,
      lastUpdate: new Date(),
    });
  }

  handleChangeRole(role) {
    const { value } = role;

    if (['admin', 'manager', 'user'].includes(value)) {
      this.setState(prevState => ({
        profile: {
          ...prevState.profile,
          role_id: null,
          role: value,
        },
      }));
    } else {
      this.setState(prevState => ({
        profile: {
          ...prevState.profile,
          role_id: value,
          role: value ? 'user' : prevState.profile.role,
        },
      }));
    }
    this.setState({ unsavedProfile: true });
  }

  handleChangeNextPasswordChangeDate(date) {
    const { profile } = this.state;
    profile.next_password_change_at = date;
    this.setState({ profile });
  }

  handleChangeAvatar(newAvatarUrl) {
    const { profile } = this.state;
    profile.avatar_reset = newAvatarUrl === null;
    this.setState({ newAvatarUrl, profile, unsavedProfile: true });
  }

  handleSaveProfile(activate = false) {
    this.setState({
      saving: true,
      errors: {},
    });

    const {
      intl,
      loadUserProfile,
      history,
    } = this.props;
    const {
      profile,
      newAvatarUrl,
      notificationSound,
      viewModeSwitcher,
      settings,
      prefixAndSuffix,
    } = this.state;

    const updatedSettings = {};
    Object.keys(settings).forEach((key) => {
      updatedSettings[key] = settings[key].value;
    });

    const profileData = {
      uuid: profile.uuid,
      first_name: profile.first_name,
      last_name: profile.last_name,
      email: profile.email,
      fields: profile.fields,
      groups: profile.groups,
      avatar_url: newAvatarUrl,
      avatar_reset: profile.avatar_reset,
      role: profile.role,
      role_id: profile.role_id,
      send_welcome_email: profile.send_welcome_email || activate,
      note: profile.note,
      next_password_change_at: profile.next_password_change_at,
      g2fa_code: profile.g2fa_code,
      notifications: profile.notifications,
      workspace_owner: profile.workspace_owner,
      settings: {
        notification_sound: notificationSound,
        view_mode_switcher: viewModeSwitcher,
        ...updatedSettings,
        file_name_prefix_and_suffix: prefixAndSuffix,
      },
    };

    if (profileData.role !== 'admin') {
      profileData.settings.view_mode_switcher = false;
    }

    if (profileData.uuid) {
      APIupdateUser(profileData).then((response) => {
        if (this.isSelfEdit()) {
          loadUserProfile();
        }
        this.setState({
          newAvatarUrl: null,
          saving: false,
        });
        if (response.data.pending_email) {
          profile.pending_email = response.data.pending_email;
          profile.email = response.data.email;
        }
        if (activate && response.data) {
          profile.activation_email_sent_at = response.data.activation_email_sent_at;
          profile.activated_by = response.data.activated_by;
        }
        if (response.data && response.data.g2fa_enabled !== profile.g2fa_enabled) {
          profile.g2fa_enabled = response.data.g2fa_enabled;
          profile.g2fa_recovery_codes = response.data.g2fa_recovery_codes;

          if (profile.g2fa_enabled) {
            sendTrackingEvent('2fa-enabled');
          }
        }

        this.handleChangePassword().then((success) => {
          if (success) {
            this.setState({
              profile,
              lastUpdate: new Date(),
            });
            addFlashMessage({
              type: 'success',
              text: intl.formatMessage({ defaultMessage: 'The changes have been saved' }),
            });
          }
          document.querySelector('.profile-edit-content').scrollTo({ top: 0, behavior: 'smooth' });
        });
        loadUserProfile();
        this.setState({ unsavedProfile: false });
      }).catch((error) => {
        this.handleErrors(error);
      });
    } else {
      if (profile.new_password) {
        profileData.password = profile.new_password;
      }
      if (profile.password_confirmation) {
        profileData.password_confirmation = profile.password_confirmation;
      }
      APIcreateUser(profileData).then((response) => {
        const { data } = response;
        this.setState({
          profile: data.uuid ? data : null,
          newAvatarUrl: null,
          saving: false,
        });
        sendTrackingEvent('user-created', { uuid: data.uuid });
        performOnboardingStep('user-invited');
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'A new user has been created' }),
        });
        this.setState({ unsavedProfile: false });
        if (data.uuid) {
          history.push(`/admin/users/${data.uuid}/edit`);
        }
      }).catch((error) => {
        this.handleErrors(error);
      });
    }
  }

  handleChangePassword() {
    const { intl } = this.props;
    const { profile } = this.state;

    if (!(profile.new_password || profile.password_confirmation)) {
      return Promise.resolve(true);
    }

    const passwords = {
      current_password: profile.current_password,
      password: profile.new_password,
      password_confirmation: profile.password_confirmation,
    };

    return APIupdatePassword(profile.uuid, passwords).then(() => {
      profile.current_password = '';
      profile.new_password = '';
      profile.password_confirmation = '';
      this.setState({
        saving: false,
        profile,
      });
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'The password has been changed' }),
      });
      return true;
    }).catch((error) => {
      this.handleSwitchTab('password');
      this.handleErrors(error);
      return false;
    });
  }

  handleSave() {
    const {
      activeTab,
      profile,
      unsavedProfile,
      unsavedShareTemplates,
    } = this.state;

    if (profile.uuid) {
      if (['profile', 'password', '2fa', 'permissions', 'notifications', 'prefix-and-suffix'].includes(activeTab)) {
        this.handleSaveProfile();
        if (unsavedShareTemplates) {
          this.handleSaveShareTemplates(true);
        }
      } else if (activeTab === 'share-settings') {
        if (unsavedProfile) {
          this.handleSaveProfile();
          this.handleSaveShareTemplates(true);
        } else {
          this.handleSaveShareTemplates();
        }
      }
    } else {
      this.handleSaveProfile();
    }
  }

  handleSaveAndInvite() {
    this.handleSaveProfile(true);
  }

  handleSendWelcomeEmails(userUuids) {
    const { intl, openConfirmationModal } = this.props;
    const modalParams = {
      title: intl.formatMessage({
        defaultMessage: 'Send welcome {userCount, plural, one {email} other {emails}}?',
      }, { userCount: userUuids.length }),
      text: intl.formatMessage({
        defaultMessage: `You are about to send a welcome email to {userCount, plural, one {user} other {users}}.
        Upon login, their {userCount, plural, one {account} other {accounts}} will be activated.`,
      }, { userCount: userUuids.length }),
      onConfirm: () => (
        sendWelcomeEmails(userUuids).then(() => {
          this.loadUserProfile(true);
        })
      ),
    };
    openConfirmationModal(modalParams);
  }

  handleRemove2FA() {
    const {
      intl,
      openConfirmationModal,
      loadUserProfile,
    } = this.props;
    const { profile } = this.state;

    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Remove two-factor authentication' }),
      text: this.isSelfEdit() ?
        intl.formatMessage({ defaultMessage: 'Are you sure you want to remove 2FA from your Stockpress account?' }) :
        intl.formatMessage({ defaultMessage: 'Are you sure you want to remove 2FA from this Stockpress account?' }),
      onConfirm: () => (
        APIremove2FA(profile.uuid).then(() => {
          loadUserProfile();
          this.loadUserProfile(true);
        })
      ),
    };
    openConfirmationModal(modalParams);
  }

  handleUnlock(e) {
    const { profile } = this.state;

    e.preventDefault();
    this.setState({ unlocking: true });
    APIunlockUser(profile.uuid).then(() => {
      this.loadUserProfile();
    });
  }

  handleSwitchGroup(groupUuid) {
    const { profile } = this.state;

    const group = profile.groups.find(sg => (sg.uuid === groupUuid));
    if (group) {
      profile.groups = profile.groups.filter(sg => (sg.uuid !== groupUuid));
    } else {
      profile.groups.push({ uuid: groupUuid });
    }
    this.setState({ profile, unsavedProfile: true });
  }

  handleNotificationChange(notificationId, channel) {
    const { profile } = this.state;
    const { notifications } = profile;

    const nIdx = notifications.findIndex(n => (n.notification_id === notificationId));
    if (nIdx !== false) {
      notifications[nIdx].channels[channel] = !notifications[nIdx].channels[channel];
    }
    this.setState({ profile, unsavedProfile: true });
  }

  handleNotificationSoundChange() {
    this.setState(prevState => ({
      notificationSound: !prevState.notificationSound,
      unsavedProfile: true,
    }));
  }

  handleSwitchViewModeSwitcher() {
    this.setState(prevState => ({
      viewModeSwitcher: !prevState.viewModeSwitcher,
      unsavedProfile: true,
    }));
  }

  handleShareTemplatesAdd(name) {
    const { templates } = this.state;
    const newUuid = uuidv4();

    this.handleSelectedTemplateUuid(newUuid);

    const newTemplates = [...templates, { uuid: newUuid, name }];

    this.setState({ templates: newTemplates, loadingAddTemplate: true }, () => {
      this.handleSave();
    });
  }

  handleShareTemplatesChange(uuid, settingKey, value) {
    this.setState(prevState => ({
      templates: prevState.templates.map(p => {
        const newTemplate = { ...p, touched: true };

        if (p.uuid === uuid) {
          if (settingKey === 'file_sizes') {
            const fileSizes = newTemplate.file_sizes || [];
            if (value.checked) {
              newTemplate[settingKey] = [...fileSizes, `${value.value}`];
            } else if (newTemplate.file_sizes.length > 1) {
              newTemplate[settingKey] = fileSizes.filter(sfs => (sfs !== `${value.value}`));
            }
          } else if (settingKey === 'logo') {
            newTemplate[settingKey] = value;
            if (value === null) {
              newTemplate.logo_url = null;
            }
          } else if (settingKey === 'logo_url') {
            newTemplate[settingKey] = value;
          } else if (settingKey === 'hide_file_details') {
            newTemplate[settingKey] = value;
            if (value) {
              newTemplate.allow_comments = false;
            }
          } else {
            newTemplate[settingKey] = value;
          }
        }
        return newTemplate;
      }),
      unsavedShareTemplates: true,
    }));
  }

  handleShareTemplatesRemove(uuid) {
    const { templates } = this.state;

    const newTemplates = templates.filter(p => p.uuid !== uuid);

    if (newTemplates.length > 0) {
      this.handleSelectedTemplateUuid(newTemplates[0].uuid);
    }

    this.setState({ templates: newTemplates, loadingRemoveTemplate: true }, () => {
      this.handleSave();
    });
  }

  handleSelectedTemplateUuid(uuid) {
    this.setState({ selectedTemplateUuid: uuid });
  }

  handlePrefixAndSuffix(type, segment, value, initialized = true) {
    const { prefixAndSuffix } = this.state;

    const prefixSuffixSetting = { ...prefixAndSuffix };

    prefixSuffixSetting[type][segment] = value;

    this.setState({ prefixAndSuffix: prefixSuffixSetting });
    if (initialized) {
      this.setState({ unsavedProfile: true });
    }
  }

  render() {
    const { intl, loggedInUser } = this.props;
    const {
      activeTab,
      profile,
      loading,
      rolesLoading,
      confirmingEmailChange,
      emailChangeConfirmed,
      emailChangeError,
      notificationSound,
      selectedTemplateUuid,
      unsavedProfile,
      unsavedShareTemplates,
    } = this.state;

    return (
      <div className={classnames('main-content profile-edit-content settings-inner-content settings-container', { 'profile-edit-content-self': this.isSelfEdit() })}>
        <GoBackToListButton />
        {!loading && (
          <AdminUserEditHeader
            profile={profile}
            isSelfEdit={this.isSelfEdit()}
          />
        )}
        {(loading || confirmingEmailChange) ? (
          <Loading />
        ) : (
          <>
            {emailChangeError && (
              <UncontrolledAlert color="danger">
                {emailChangeError}
              </UncontrolledAlert>
            )}
            {profile.pending_email && (
              <Alert color="warning">
                {intl.formatMessage({
                  defaultMessage: 'There is a pending change of your email to {email}.',
                }, { email: profile.pending_email })}
                {' '}
                <a href="#cancel" onClick={this.handleCancelEmailChange}>{intl.formatMessage({ defaultMessage: 'Cancel' })}</a>
              </Alert>
            )}
            {emailChangeConfirmed && (
              <UncontrolledAlert color="success">
                {intl.formatMessage({ defaultMessage: 'Your email change has been confirmed' })}
              </UncontrolledAlert>
            )}
            <AdminUserEditTabs
              {...this.state}
              isSelfEdit={this.isSelfEdit()}
              activeTab={activeTab}
              handleSwitchTab={this.handleSwitchTab}
            />
            <ErrorBoundary>
              <AdminUserEditTabContent
                {...this.state}
                isSelfEdit={this.isSelfEdit()}
                loggedInUser={loggedInUser}
                rolesLoading={rolesLoading}
                handleChange={this.handleChange}
                handleChangeAvatar={this.handleChangeAvatar}
                handleChangeRole={this.handleChangeRole}
                handleChangeNextPasswordChangeDate={this.handleChangeNextPasswordChangeDate}
                handleSave={this.handleSave}
                handleSaveAndInvite={
                    (!profile.uuid || profile.activation_email_sent_at || this.isSelfEdit() || profile.role === 'admin') ?
                      null :
                      this.handleSaveAndInvite
                  }
                loadUserProfile={this.loadUserProfile}
                handleSendWelcomeEmails={this.handleSendWelcomeEmails}
                handleSwitchGroup={this.handleSwitchGroup}
                handleRemove2FA={this.handleRemove2FA}
                handleUnlock={this.handleUnlock}
                handleCancelEmailChange={this.handleCancelEmailChange}
                handleNotificationChange={this.handleNotificationChange}
                notificationSound={notificationSound}
                handleNotificationSoundChange={this.handleNotificationSoundChange}
                handleSwitchViewModeSwitcher={this.handleSwitchViewModeSwitcher}
                handleShareTemplatesAdd={this.handleShareTemplatesAdd}
                handleShareTemplatesChange={this.handleShareTemplatesChange}
                handleShareTemplatesRemove={this.handleShareTemplatesRemove}
                handleSelectedTemplateUuid={this.handleSelectedTemplateUuid}
                handlePrefixAndSuffix={this.handlePrefixAndSuffix}
                selectedTemplateUuid={selectedTemplateUuid}
                unsaved={unsavedProfile || unsavedShareTemplates}
              />
            </ErrorBoundary>
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  loggedInUser: state.Auth.profile,
  availableFileSizes: state.App.fileSizes,
});

const mapDispatchToProps = dispatch => ({
  loadUserProfile: () => {
    dispatch(fetchUserProfile());
  },
  openConfirmationModal: (params) => {
    dispatch(showConfirmationModal(params));
  },
});

ProfileEdit.propTypes = {
  intl: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  loadUserProfile: PropTypes.func.isRequired,
  openConfirmationModal: PropTypes.func.isRequired,
  availableFileSizes: PropTypes.array.isRequired,
  loggedInUser: PropTypes.object,
};

ProfileEdit.defaultProps = {
  loggedInUser: null,
};

export default withRouter(injectIntl(connect(mapStateToProps, mapDispatchToProps)(ProfileEdit)));
